import { useEffect, useState } from "react";
import { Trophy, Car, Building2, Coins, FileWarning, BadgeMinus } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend } from "recharts";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { eur2 } from "@/lib/format";

const ALL = "all";
const METRICS = { count: "Nombre", amount: "Montant", points: "Points" };

const Board = ({ title, icon: Icon, rows, metric, onPick, testid }) => (
  <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid={testid}>
    <h3 className="mb-3 flex items-center gap-2 font-heading text-base font-semibold text-[#18221F]"><Icon className="h-4 w-4 text-[#A5894E]" />{title}</h3>
    {rows.length === 0 ? <p className="py-6 text-center text-sm text-[#8695AB]">Aucune donnée</p> : (
      <ol className="space-y-1">
        {[...rows].sort((a, b) => b[metric] - a[metric]).map((r, i) => (
          <li key={r.id}>
            <button onClick={() => onPick(r)} className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left text-sm transition-colors hover:bg-[#F5F7FA]" data-testid={`${testid}-row`}>
              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${i === 0 ? "bg-[#A5894E] text-white" : "bg-[#EEF2F8] text-[#001A3B]"}`}>{i + 1}</span>
              <span className="min-w-0 flex-1 truncate text-[#18221F]">{r.label}</span>
              <span className="shrink-0 text-xs text-[#8695AB]">{r.count} PV · {r.points} pts</span>
              <span className="w-24 shrink-0 text-right font-semibold text-[#001A3B]">{eur2(r.amount)}</span>
            </button>
          </li>
        ))}
      </ol>
    )}
  </div>
);

export default function ContraventionStats({ onFilterList }) {
  const [f, setF] = useState({ start: "", end: "", company: ALL, category: ALL });
  const [metric, setMetric] = useState("count");
  const [d, setD] = useState(null);

  useEffect(() => {
    const p = new URLSearchParams();
    if (f.start) p.set("start", f.start);
    if (f.end) p.set("end", f.end);
    if (f.company !== ALL) p.set("company", f.company);
    if (f.category !== ALL) p.set("category", f.category);
    api.get(`/contraventions/stats?${p.toString()}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [f]);

  const pick = (key, value) => {
    const extra = { [key]: value };
    if (f.start) extra.du = f.start;
    if (f.end) extra.au = f.end;
    if (f.company !== ALL && key !== "entite") extra.entite = f.company;
    if (f.category !== ALL) extra.categorie = f.category;
    onFilterList(extra);
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-end gap-3 rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid="cv-stats-filters">
        <div><Label className="mb-1 block text-xs text-[#4A5568]">Du</Label><Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="w-40" data-testid="cv-stats-start" /></div>
        <div><Label className="mb-1 block text-xs text-[#4A5568]">Au</Label><Input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="w-40" data-testid="cv-stats-end" /></div>
        <div><Label className="mb-1 block text-xs text-[#4A5568]">Entité</Label>
          <Select value={f.company} onValueChange={(v) => setF({ ...f, company: v })}><SelectTrigger className="w-48" data-testid="cv-stats-company"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>Toutes les entités</SelectItem>{(d?.companies || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="mb-1 block text-xs text-[#4A5568]">Type</Label>
          <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger className="w-48" data-testid="cv-stats-category"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>Tous les types</SelectItem><SelectItem value="infraction">Infraction</SelectItem><SelectItem value="stationnement">Stationnement (FPS)</SelectItem></SelectContent></Select></div>
        <div><Label className="mb-1 block text-xs text-[#4A5568]">Classer par</Label>
          <Select value={metric} onValueChange={setMetric}><SelectTrigger className="w-36" data-testid="cv-stats-metric"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(METRICS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></div>
        <Button variant="outline" onClick={() => setF({ start: "", end: "", company: ALL, category: ALL })} data-testid="cv-stats-reset">Réinitialiser</Button>
      </div>

      {!d ? <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-64 rounded-2xl" />)}</div> : (
        <>
          <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {[[FileWarning, "Contraventions", d.total, "cv-stats-total"], [Coins, "Montant cumulé", eur2(d.total_amount), "cv-stats-amount"], [BadgeMinus, "Points retirés", d.total_points, "cv-stats-points"]].map(([Icon, l, v, t]) => (
              <div key={t} className="rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid={t}>
                <Icon className="h-5 w-5 text-[#A5894E]" /><div className="mt-2 font-heading text-2xl font-bold text-[#18221F]">{v}</div><div className="text-xs text-[#8695AB]">{l}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
            <Board title="Top conducteurs" icon={Trophy} rows={d.top_drivers} metric={metric} onPick={(r) => pick("conducteur", r.id)} testid="cv-board-drivers" />
            <Board title="Top véhicules" icon={Car} rows={d.top_vehicles} metric={metric} onPick={(r) => pick("vehicule", r.id)} testid="cv-board-vehicles" />
            <Board title="Top entités" icon={Building2} rows={d.top_companies} metric={metric} onPick={(r) => pick("entite", r.id)} testid="cv-board-companies" />
          </div>

          <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5">
              <h3 className="mb-3 font-heading text-base font-semibold text-[#18221F]">Top conducteurs — {METRICS[metric].toLowerCase()}</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[...d.top_drivers].sort((a, b) => b[metric] - a[metric])} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EDF1F6" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#8695AB" }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="label" width={120} tick={{ fontSize: 11, fill: "#4A5568" }} axisLine={false} tickLine={false} />
                    <Tooltip formatter={(v) => (metric === "amount" ? eur2(v) : v)} />
                    <Bar dataKey={metric} name={METRICS[metric]} fill="#001A3B" radius={[0, 5, 5, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5">
              <h3 className="mb-3 font-heading text-base font-semibold text-[#18221F]">Évolution mensuelle</h3>
              <div className="h-64">
                {d.monthly.length === 0 ? <p className="py-20 text-center text-sm text-[#8695AB]">Aucune donnée</p> : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={d.monthly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDF1F6" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#8695AB" }} axisLine={false} tickLine={false} />
                      <YAxis yAxisId="l" allowDecimals={false} tick={{ fontSize: 11, fill: "#8695AB" }} axisLine={false} tickLine={false} width={30} />
                      <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#8695AB" }} axisLine={false} tickLine={false} width={50} />
                      <Tooltip formatter={(v, n) => (n === "Montant" ? eur2(v) : v)} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line yAxisId="l" type="monotone" dataKey="count" name="Nombre" stroke="#001A3B" strokeWidth={2.5} dot={{ r: 3 }} />
                      <Line yAxisId="r" type="monotone" dataKey="amount" name="Montant" stroke="#A5894E" strokeWidth={2.5} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
